import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  getNotificaciones, marcarTodasLeidas, marcarLeida, borrarNotificacion, borrarTodasNotificaciones,
} from "../../features/portal/services/portalService";

// Sondeo sin websockets. La administración cada 4s (se siente casi inmediato);
// el residente cada 30s: puede haber cientos conectados y para sus avisos no
// hace falta más. Con la pestaña oculta no se sondea (ver más abajo).
const POLL_ADMIN = 4000;
const POLL_RESIDENTE = 30000;

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifs,      setNotifs]      = useState([]);
  const [unread,      setUnread]      = useState(0);
  const [justArrived, setJustArrived] = useState([]);
  const seenIds = useRef(null); // null = todavía no hicimos el primer sondeo

  const poll = useCallback(async () => {
    try {
      const data = await getNotificaciones();
      const list = data.results || [];
      setNotifs(list);
      setUnread(data.unread || 0);

      if (seenIds.current !== null) {
        const nuevas = list.filter(n => !seenIds.current.has(n.id));
        if (nuevas.length > 0) {
          setJustArrived(prev => [...prev, ...nuevas]);
          // Si hay alguien viendo la página relacionada, que se refresque sola
          // en vez de esperar a que la persona la recargue a mano.
          nuevas.forEach(n => {
            window.dispatchEvent(new CustomEvent("portal:data-changed", { detail: { tipo: n.tipo, link: n.link } }));
          });
        }
      }
      seenIds.current = new Set(list.map(n => n.id));
    } catch { /* silencioso — no queremos que un poll fallido rompa la pantalla */ }
  }, []);

  useEffect(() => {
    // El operador de agua no tiene acceso al portal: sondearlo solo generaría
    // un 403 cada pocos segundos, sin ninguna notificación que mostrar.
    const rol = localStorage.getItem("rol");
    if (["AGUA", "SEGURIDAD"].includes(rol)) return undefined;
    const intervalo = rol === "RESIDENTE" ? POLL_RESIDENTE : POLL_ADMIN;
    // Pestaña en segundo plano: no se gasta tráfico. Al volver, se pone al día.
    const tick = () => { if (!document.hidden) poll(); };
    const alVolver = () => { if (!document.hidden) poll(); };
    poll();
    const timer = setInterval(tick, intervalo);
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [poll]);

  const dismissArrived = useCallback((id) => {
    setJustArrived(prev => prev.filter(n => n.id !== id));
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await marcarTodasLeidas();
      setUnread(0);
      setNotifs(prev => prev.map(n => ({ ...n, leida: true })));
    } catch { /* silencioso */ }
  }, []);

  const markOneRead = useCallback(async (id) => {
    try {
      await marcarLeida(id);
      setNotifs(prev => prev.map(n => (n.id === id ? { ...n, leida: true } : n)));
    } catch { /* silencioso */ }
  }, []);

  const deleteOne = useCallback(async (id) => {
    const antes = notifs;
    setNotifs(prev => prev.filter(n => n.id !== id));
    try {
      await borrarNotificacion(id);
    } catch {
      setNotifs(antes);   // si falló, vuelve a aparecer
    }
  }, [notifs]);

  const deleteAll = useCallback(async () => {
    try {
      await borrarTodasNotificaciones();
      setNotifs([]);
      setUnread(0);
    } catch { /* silencioso */ }
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifs, unread, justArrived, dismissArrived, markAllRead, markOneRead, deleteOne, deleteAll, refetch: poll,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications debe usarse dentro de <NotificationProvider>");
  return ctx;
}
